import React from 'react';
import { Box, Paper, Typography, Skeleton, SxProps } from '@mui/material';
import { Warning, CheckCircle, Schedule, Cancel, Visibility } from '@mui/icons-material';
import { Theme, alpha, useTheme } from '@mui/material/styles';
import { AlertStatus } from '../types/alerts';
import { getCardBorder } from '../DashboardV2/types';

// Props interface for the AlertStatistics component
interface AlertStatisticsProps {
  alertStatus: AlertStatus | null;
  sx?: SxProps<Theme>;
}

// Props interface for the StatCard subcomponent
interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

/**
 * StatCard displays a single statistic card with an icon, value, and description.
 * Styled to match the Overview tab's GlobalStatsRow cards.
 */
const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 100,
        borderRadius: 2,
        border: getCardBorder(theme),
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: theme.shadows[4],
          borderColor: alpha(color, 0.5),
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {/* Icon container - matches Overview pattern */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(color, 0.1),
            color: color,
            mr: 1.5,
          }}
        >
          {icon}
        </Box>

        {/* Text content - caption title on top, h5 value below */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {value}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

/**
 * AlertStatistics displays a grid of alert statistics cards showing
 * total, new, acknowledged, and resolved alert counts.
 */
export const AlertStatistics: React.FC<AlertStatisticsProps> = ({
  alertStatus,
  sx = {}
}) => {
  const theme = useTheme();

  // Show skeleton loading UI when data is not available
  if (!alertStatus) {
    return (
      <Box sx={{ ...sx }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
            gap: 2,
          }}
        >
          {[0, 1, 2, 3, 4].map((index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{ p: 2, borderRadius: 2, border: getCardBorder(theme) }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Skeleton animation="wave" variant="rounded" width={40} height={40} sx={{ mr: 1.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton animation="wave" variant="text" width={80} height={16} />
                  <Skeleton animation="wave" variant="text" width={60} height={32} />
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  // Extract alert counts from the status data with null safety
  const alerts = alertStatus?.alerts ?? { total: 0, new: 0, acknowledged: 0, auto_resolved: 0, resolved: 0 };

  return (
    <Box sx={{ ...sx }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 2,
        }}
      >
        <StatCard
          title="Total Alerts"
          value={alerts.total ?? 0}
          icon={<Warning />}
          color={theme.palette.grey[700]}
        />
        <StatCard
          title="New Alerts"
          value={alerts.new ?? 0}
          icon={<Cancel />}
          color={theme.palette.error.main}
        />
        <StatCard
          title="Acknowledged"
          value={alerts.acknowledged ?? 0}
          icon={<Schedule />}
          color={theme.palette.warning.main}
        />
        <StatCard
          title="Needs Review"
          value={alerts.auto_resolved ?? 0}
          icon={<Visibility />}
          color={theme.palette.info.main}
        />
        <StatCard
          title="Resolved"
          value={alerts.resolved ?? 0}
          icon={<CheckCircle />}
          color={theme.palette.success.main}
        />
      </Box>
    </Box>
  );
};
